import {
    Button,
    IPublicReservationForReview,
    ReservationStatus,
    SERVER_DATE_FORMAT,
    Spinner,
    StringUtils,
    TIME_FORMAT,
    useConfirmContext,
    useUrlParams,
} from '@localina/core';
import { CookieIcon } from '@localina/icons';
import { Dialog } from '@mui/material';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { usePublicReservationByToken, useUpdateReservationStatusByToken } from '../../api/queries/reservations';
import { GuestStatusIcon } from '../../components/GuestStatusIcon';
import { Path } from '../../enums';
import { PathUtils } from '../../utils';

enum QueryParamAction {
    APPROVE = 'approve',
    DECLINE = 'decline',
}

const STROKE_ICON_PROPS = {
    width: 16,
    height: 16,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
} as const;

const CheckGlyph = (props: { size?: number }) => (
    <svg {...STROKE_ICON_PROPS} width={props.size ?? 16} height={props.size ?? 16} strokeWidth={2.2}>
        <path d="m5 12.5 4.5 4.5L19 7.5" />
    </svg>
);

const CrossGlyph = (props: { size?: number }) => (
    <svg {...STROKE_ICON_PROPS} width={props.size ?? 16} height={props.size ?? 16} strokeWidth={2}>
        <path d="m6 6 12 12M18 6 6 18" />
    </svg>
);

const NoteGlyph = () => (
    <svg {...STROKE_ICON_PROPS} strokeWidth={1.8}>
        <path d="M6 3.8h8.5L18 7.3V20H6Z" />
        <path d="M14 3.8v3.7h3.7" />
        <path d="M9 12h6M9 15.5h4" />
    </svg>
);

const ArrowGlyph = () => (
    <svg {...STROKE_ICON_PROPS} width={15} height={15} strokeWidth={1.8}>
        <path d="M5 12h13M12.5 6l6 6-6 6" />
    </svg>
);

// the times as each language writes them, e.g. "09h42" in French
const TIME_FORMATS: Record<string, string> = { fr: "HH'h'mm" };

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

const STATUS_TAG: Partial<Record<ReservationStatus, { type: string; key: string }>> = {
    [ReservationStatus.APPROVAL_PENDING]: { type: 'open', key: 'open' },
    [ReservationStatus.APPROVED]: { type: 'ok', key: 'accepted' },
    [ReservationStatus.DECLINED]: { type: 'crit', key: 'declined' },
    [ReservationStatus.GUEST_CANCELLED]: { type: 'crit', key: 'guestCancelled' },
};

const RESULT_BANNER: Partial<Record<ReservationStatus, { type: 'ok' | 'crit'; key: string }>> = {
    [ReservationStatus.APPROVED]: { type: 'ok', key: 'accepted' },
    [ReservationStatus.DECLINED]: { type: 'crit', key: 'declined' },
    [ReservationStatus.GUEST_CANCELLED]: { type: 'crit', key: 'guestCancelled' },
};

const ReviewPendingReservation = () => {
    const { t, i18n } = useTranslation();
    const [urlParams, setUrlParams] = useUrlParams();
    const actionInUrl = urlParams.get('action');
    const restaurantId = urlParams.get('restaurantId');
    const reservationToken = urlParams.get('token');

    const { snackbar, copyOrLinkDialog } = useConfirmContext();
    const navigate = useNavigate();

    const [actionToConfirm, setActionToConfirm] = useState<QueryParamAction | null>(null);

    const reservationQuery = usePublicReservationByToken(restaurantId || '', reservationToken || '', {
        enabled: Boolean(reservationToken && restaurantId),
    });
    const updateReservationStatusByTokenMutation = useUpdateReservationStatusByToken();

    const reservation = reservationQuery.data;

    const reservationIsValidForReviewPage =
        reservation &&
        [
            ReservationStatus.APPROVAL_PENDING,
            ReservationStatus.DECLINED,
            ReservationStatus.APPROVED,
            ReservationStatus.GUEST_CANCELLED,
        ].includes(reservation.status);
    const isProcessed = reservation && reservation.status !== ReservationStatus.APPROVAL_PENDING;
    const isLoading = reservationQuery.isInitialLoading || updateReservationStatusByTokenMutation.isLoading;

    // the restaurant's reservation list, on the day of the request once it has loaded
    const openReservations = () => {
        const date = reservation && DateTime.fromISO(reservation.reservationDateTime).toFormat(SERVER_DATE_FORMAT);
        navigate(
            PathUtils.generateUrl(
                Path.RESTAURANT_RESERVATIONS,
                { restaurantId: restaurantId ?? undefined },
                date ? { date } : undefined,
            ),
        );
    };

    const clearUrlParams = useCallback(() => {
        setUrlParams({ action: undefined });
    }, []);

    const askToConfirm = (action: QueryParamAction) => () => {
        if (!isProcessed) {
            setActionToConfirm(action);
        }
    };

    const closeConfirmDialog = () => {
        setActionToConfirm(null);
        clearUrlParams();
    };

    const updateReservationStatus = (status: ReservationStatus) => {
        if (reservation && reservationToken && restaurantId) {
            updateReservationStatusByTokenMutation.mutate(
                { status, token: reservationToken, restaurantId, reservationId: reservation.reservationId },
                {
                    // the result banner on the card says what happened
                    onSuccess: () => reservationQuery.refetch(),
                    onError: () => {
                        snackbar({
                            msg: t('common.infoMessages.commonError'),
                            severity: 'error',
                        });
                        return reservationQuery.refetch();
                    },
                },
            );
        }
    };

    const confirmAction = () => {
        if (actionToConfirm) {
            updateReservationStatus(
                actionToConfirm === QueryParamAction.APPROVE ? ReservationStatus.APPROVED : ReservationStatus.DECLINED,
            );
        }
        closeConfirmDialog();
    };

    useEffect(() => {
        if (reservation) {
            const action = Object.values(QueryParamAction).find((a) => a === actionInUrl);
            if (action && reservation.status === ReservationStatus.APPROVAL_PENDING) {
                setActionToConfirm(action);
            } else {
                clearUrlParams();
            }
        }
    }, [actionInUrl, reservation]);

    const locale = i18n.language === 'en' ? 'en-GB' : i18n.language;
    const timeFormat = TIME_FORMATS[i18n.language] ?? TIME_FORMAT;
    const reservationDateTime = reservation && DateTime.fromISO(reservation.reservationDateTime).setLocale(locale);
    const facts = reservationDateTime && {
        date: capitalize(reservationDateTime.toFormat('ccc dd.MM.yyyy')),
        time: reservationDateTime.toFormat(TIME_FORMAT),
        participants:
            reservation.participants !== undefined
                ? t('reservations.reviewPendingReservation.participants', { count: reservation.participants })
                : undefined,
        area: reservation.area?.name,
    };

    return (
        <div className="review-request">
            <div className="review-request__column">
                {reservation && reservationIsValidForReviewPage && facts && (
                    <section className="review-request__card">
                        <ReviewRequestHeading reservation={reservation} locale={locale} timeFormat={timeFormat} />

                        <dl className="review-request__facts">
                            <div>
                                <dt>{t('reservations.reviewPendingReservation.facts.date')}</dt>
                                <dd>{facts.date}</dd>
                            </div>
                            <div>
                                <dt>{t('reservations.reviewPendingReservation.facts.time')}</dt>
                                <dd>{facts.time}</dd>
                            </div>
                            {facts.participants && (
                                <div>
                                    <dt>{t('reservations.reviewPendingReservation.facts.guests')}</dt>
                                    <dd className="highlight">{facts.participants}</dd>
                                </div>
                            )}
                            {facts.area && (
                                <div>
                                    <dt>{t('reservations.reviewPendingReservation.facts.area')}</dt>
                                    <dd>{facts.area}</dd>
                                </div>
                            )}
                        </dl>

                        {reservation.guestInfo && (
                            <ReviewRequestGuest
                                guestInfo={reservation.guestInfo}
                                onPhoneNumberClick={(value) => copyOrLinkDialog({ type: 'phoneNumber', value })}
                                onEmailClick={(value) => copyOrLinkDialog({ type: 'email', value })}
                            />
                        )}

                        {Boolean(reservation.comment?.trim()) && (
                            <div className="review-request__note">
                                <span className="review-request__note__icon">
                                    <NoteGlyph />
                                </span>
                                <p>
                                    <b>{t('reservations.reviewPendingReservation.guestNote')}</b>
                                    {reservation.comment}
                                </p>
                            </div>
                        )}

                        {isProcessed ? (
                            <ReviewRequestResult status={reservation.status} />
                        ) : (
                            <div className="review-request__actions">
                                <div className="decline">
                                    <Button
                                        label={t('reservations.reviewPendingReservation.actions.decline')}
                                        startIcon={<CrossGlyph />}
                                        onClick={askToConfirm(QueryParamAction.DECLINE)}
                                        secondary
                                    />
                                </div>
                                <div className="accept">
                                    <Button
                                        label={t('reservations.reviewPendingReservation.actions.accept')}
                                        startIcon={<CheckGlyph />}
                                        onClick={askToConfirm(QueryParamAction.APPROVE)}
                                    />
                                </div>
                            </div>
                        )}
                    </section>
                )}
                {!reservation && !isLoading && (
                    <section className="review-request__card review-request__card--message">
                        <p>{t('reservations.reviewPendingReservation.reservationNotFound')}</p>
                    </section>
                )}
                {reservation && !reservationIsValidForReviewPage && (
                    <section className="review-request__card review-request__card--message">
                        <p>{t('reservations.reviewPendingReservation.reservationIsNotValidForReview')}</p>
                    </section>
                )}
                <button type="button" className="review-request__app-link" onClick={openReservations}>
                    {t('reservations.reviewPendingReservation.openApp')}
                    <ArrowGlyph />
                </button>
            </div>

            {reservation && facts && (
                <ReviewRequestConfirmDialog
                    action={actionToConfirm}
                    summary={[facts.date, facts.time, facts.participants, facts.area]}
                    onConfirm={confirmAction}
                    onClose={closeConfirmDialog}
                />
            )}
            {isLoading && <Spinner />}
        </div>
    );
};

interface IReviewRequestHeadingProps {
    reservation: IPublicReservationForReview;
    locale: string;
    timeFormat: string;
}

const ReviewRequestHeading = ({ reservation, locale, timeFormat }: IReviewRequestHeadingProps) => {
    const { t } = useTranslation();
    const tag = STATUS_TAG[reservation.status];

    const received = (() => {
        if (!reservation.createdAt) {
            return null;
        }
        const createdAt = DateTime.fromISO(reservation.createdAt).setLocale(locale);
        const time = createdAt.toFormat(timeFormat);
        const today = DateTime.now().startOf('day');
        if (createdAt >= today) {
            return t('reservations.reviewPendingReservation.received.today', { time });
        }
        if (createdAt >= today.minus({ days: 1 })) {
            return t('reservations.reviewPendingReservation.received.yesterday', { time });
        }
        return t('reservations.reviewPendingReservation.received.date', {
            date: createdAt.toFormat('ccc dd.MM.yyyy'),
            time,
        });
    })();

    return (
        <div className="review-request__heading">
            <div>
                <h1>{t('reservations.reviewPendingReservation.heading')}</h1>
                {received && <p>{received}</p>}
            </div>
            {tag && (
                <span className={StringUtils.combineStrings(['review-request__tag', tag.type])}>
                    {t(`reservations.reviewPendingReservation.status.${tag.key}`)}
                </span>
            )}
        </div>
    );
};

interface IReviewRequestGuestProps {
    guestInfo: IPublicReservationForReview['guestInfo'];
    onPhoneNumberClick: (value: string) => void;
    onEmailClick: (value: string) => void;
}

const ReviewRequestGuest = ({ guestInfo, onPhoneNumberClick, onEmailClick }: IReviewRequestGuestProps) => {
    const { t } = useTranslation();
    const name = StringUtils.combineStrings([guestInfo.firstName, guestInfo.lastName]);
    const phoneNumber = guestInfo.phoneNumbers?.[0];
    const email = guestInfo.email;

    return (
        <>
            <h2 className="review-request__section-title">{t('reservations.reviewPendingReservation.guest.title')}</h2>
            <dl className="review-request__rows">
                {name && (
                    <div>
                        <dt>{t('reservations.reviewPendingReservation.guest.name')}</dt>
                        <dd className="guest-name">
                            {name}
                            <GuestStatusIcon guestStatus={guestInfo.guestStatus} />
                        </dd>
                    </div>
                )}
                {guestInfo.company && (
                    <div>
                        <dt>{t('reservations.reviewPendingReservation.guest.company')}</dt>
                        <dd>{guestInfo.company}</dd>
                    </div>
                )}
                {guestInfo.allergies && (
                    <div>
                        <dt>{t('reservations.reviewPendingReservation.guest.allergies')}</dt>
                        <dd className="allergies">
                            <CookieIcon />
                            {guestInfo.allergies}
                        </dd>
                    </div>
                )}
                {phoneNumber && (
                    <div>
                        <dt>{t('reservations.reviewPendingReservation.guest.phone')}</dt>
                        <dd>
                            <button
                                type="button"
                                className="phone-number"
                                onClick={() => onPhoneNumberClick(phoneNumber)}
                            >
                                {phoneNumber}
                            </button>
                        </dd>
                    </div>
                )}
                {email && (
                    <div>
                        <dt>{t('reservations.reviewPendingReservation.guest.email')}</dt>
                        <dd>
                            <button type="button" onClick={() => onEmailClick(email)}>
                                {email}
                            </button>
                        </dd>
                    </div>
                )}
            </dl>
        </>
    );
};

const ReviewRequestResult = ({ status }: { status: ReservationStatus }) => {
    const { t } = useTranslation();
    const banner = RESULT_BANNER[status];

    return banner ? (
        <div className={StringUtils.combineStrings(['review-request__banner', banner.type])} role="status">
            <span className="review-request__banner__icon">
                {banner.type === 'ok' ? <CheckGlyph size={17} /> : <CrossGlyph size={17} />}
            </span>
            <p>
                <b>{t(`reservations.reviewPendingReservation.result.${banner.key}.title`)}</b>
                {t(`reservations.reviewPendingReservation.result.${banner.key}.text`)}
            </p>
        </div>
    ) : null;
};

interface IReviewRequestConfirmDialogProps {
    action: QueryParamAction | null;
    summary: (string | undefined)[];
    onConfirm: () => void;
    onClose: () => void;
}

const ReviewRequestConfirmDialog = ({ action, summary, onConfirm, onClose }: IReviewRequestConfirmDialogProps) => {
    const { t } = useTranslation();
    // keep the last action while the dialog fades out, so its text doesn't switch mid-close
    const [shownAction, setShownAction] = useState(action);
    useEffect(() => {
        if (action) {
            setShownAction(action);
        }
    }, [action]);

    const isAccepting = shownAction !== QueryParamAction.DECLINE;
    const key = isAccepting ? 'accept' : 'decline';

    return (
        <Dialog
            open={Boolean(action)}
            onClose={onClose}
            className={StringUtils.combineStrings(['review-request-dialog', isAccepting ? 'ok' : 'crit'])}
            aria-labelledby="review-request-dialog-title"
        >
            <span className="review-request-dialog__icon">
                {isAccepting ? <CheckGlyph size={22} /> : <CrossGlyph size={22} />}
            </span>
            <h2 id="review-request-dialog-title">{t(`reservations.reviewPendingReservation.confirm.${key}.title`)}</h2>
            <p>{t(`reservations.reviewPendingReservation.confirm.${key}.text`)}</p>
            <div className="review-request-dialog__summary">
                {summary
                    .filter((part): part is string => Boolean(part))
                    .map((part, index) => (
                        <React.Fragment key={index}>
                            {index > 0 && <i aria-hidden>·</i>}
                            <span>{part}</span>
                        </React.Fragment>
                    ))}
            </div>
            <div className="review-request-dialog__buttons">
                <Button label={t('reservations.reviewPendingReservation.confirm.cancel')} onClick={onClose} secondary />
                <Button label={t(`reservations.reviewPendingReservation.confirm.${key}.button`)} onClick={onConfirm} />
            </div>
        </Dialog>
    );
};

export default ReviewPendingReservation;
